/** True when running inside Acekil Android/iOS WebView (native bridge injected). */
export function isHybridShell(): boolean {
  return typeof window !== 'undefined' && typeof (window as any).Android !== 'undefined';
}

/**
 * Ask the native shell to clear Keychain/session and show the native voice gate.
 * Returns true when the bridge handled it (do not route to SPA /login).
 */
export function requestNativeHybridSignOut(): boolean {
  if (!isHybridShell()) {
    return false;
  }
  try {
    const android = (window as any).Android;
    if (typeof android?.onSignOut === 'function') {
      android.onSignOut();
      return true;
    }
  } catch {
    return false;
  }
  return false;
}

/** True for iPhone/iPad hybrid WebView (not Android). */
export function isHybridIosShell(): boolean {
  if (!isHybridShell()) {
    return false;
  }
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent || '' : '';
  return /iPhone|iPad|iPod/i.test(ua) || (/Macintosh/i.test(ua) && typeof document !== 'undefined' && 'ontouchend' in document);
}

/** True for Windows hybrid WebView (WebView2 shell, not iOS/Android). */
export function isHybridWindowsShell(): boolean {
  if (!isHybridShell()) {
    return false;
  }
  if (isHybridIosShell()) {
    return false;
  }
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent || '' : '';
  return /Windows/i.test(ua) && !/Android/i.test(ua);
}

export interface OpenBlackboardPayload {
  boardId: string;
  company?: string;
  tokenHint?: string;
}

/**
 * Open native Blackboard when in a hybrid shell; otherwise false (caller should route to SPA).
 */
export function tryOpenNativeBlackboard(payload: OpenBlackboardPayload): boolean {
  if (!isHybridShell()) return false;
  const message = JSON.stringify({
    boardId: payload.boardId || 'default',
    company: payload.company || '',
    tokenHint: payload.tokenHint || '',
  });
  try {
    if (typeof window.Android?.openBlackboard === 'function') {
      window.Android.openBlackboard(message);
      return true;
    }
  } catch {
    return false;
  }
  return false;
}
